import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/utils/cn';

// ============================================
// TYPES
// ============================================

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string | undefined;
  action?: ToastAction | undefined;
  duration?: number | undefined;
}

interface ToastContextValue {
  toasts: Toast[];
  toast: (options: Omit<Toast, 'id'>) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string, action?: ToastAction) => void;
  info: (title: string, description?: string) => void;
  dismiss: (id: string) => void;
}

// ============================================
// STYLES
// ============================================

const typeStyles: Record<ToastType, string> = {
  success: 'border-green-500 bg-green-50',
  error: 'border-red-500 bg-red-50',
  warning: 'border-yellow-500 bg-yellow-50',
  info: 'border-blue-500 bg-blue-50',
};

const typeIcons: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const iconStyles: Record<ToastType, string> = {
  success: 'text-green-500',
  error: 'text-red-500',
  warning: 'text-yellow-500',
  info: 'text-blue-500',
};

// ============================================
// CONTEXT
// ============================================

const ToastContext = createContext<ToastContextValue | null>(null);

// ============================================
// PROVIDER
// ============================================

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((options: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { ...options, id }]);
  }, []);

  const success = useCallback(
    (title: string, description?: string) => {
      toast({ type: 'success', title, description, duration: 3000 });
    },
    [toast]
  );

  const error = useCallback(
    (title: string, description?: string) => {
      toast({ type: 'error', title, description, duration: 5000 });
    },
    [toast]
  );

  const warning = useCallback(
    (title: string, description?: string, action?: ToastAction) => {
      toast({ type: 'warning', title, description, action, duration: 5000 });
    },
    [toast]
  );

  const info = useCallback(
    (title: string, description?: string) => {
      toast({ type: 'info', title, description, duration: 4000 });
    },
    [toast]
  );

  return (
    <ToastContext.Provider value={{ toasts, toast, success, error, warning, info, dismiss }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
        <ToastPrimitive.Viewport
          className={cn(
            'fixed bottom-0 right-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4',
            'sm:bottom-4 sm:right-4 sm:top-auto sm:flex-col sm:max-w-[420px]'
          )}
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

// ============================================
// TOAST ITEM
// ============================================

interface ToastItemProps {
  toast: Toast;
  onDismiss: () => void;
}

function ToastItem({ toast: t, onDismiss }: ToastItemProps) {
  const Icon = typeIcons[t.type];

  return (
    <ToastPrimitive.Root
      duration={t.duration ?? 5000}
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
      className={cn(
        'group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden',
        'rounded-lg border-l-4 p-4 shadow-lg transition-all',
        'data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]',
        'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none',
        'data-[state=open]:animate-slide-up data-[state=closed]:animate-fade-out',
        typeStyles[t.type]
      )}
    >
      <Icon className={cn('h-5 w-5 flex-shrink-0 mt-0.5', iconStyles[t.type])} />

      <div className="flex-1 space-y-1">
        <ToastPrimitive.Title className="text-sm font-semibold text-[var(--text-primary)]">
          {t.title}
        </ToastPrimitive.Title>
        {t.description && (
          <ToastPrimitive.Description className="text-sm text-[var(--text-secondary)]">
            {t.description}
          </ToastPrimitive.Description>
        )}
        {t.action && (
          <ToastPrimitive.Action altText={t.action.label} asChild>
            <button
              type="button"
              onClick={t.action.onClick}
              className="text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              {t.action.label}
            </button>
          </ToastPrimitive.Action>
        )}
      </div>

      <ToastPrimitive.Close
        className={cn(
          'absolute right-2 top-2 rounded-md p-1 text-[var(--text-tertiary)]',
          'opacity-0 transition-opacity hover:text-[var(--text-secondary)]',
          'group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-brand-500'
        )}
      >
        <X className="h-4 w-4" />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}

// ============================================
// HOOK
// ============================================

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
}

// ============================================
// STANDALONE TOASTER (re-export provider)
// ============================================

export { ToastProvider as Toaster };
